const GPS_TIME_ZONE = "Europe/Warsaw"
const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

const ISO_TIME =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:?\d{2})?$/

const wallClockFormatters = new Map<string, Intl.DateTimeFormat>()

const wallClockFormatter = (timeZone: string | undefined) => {
  const key = timeZone ?? ""
  const cached = wallClockFormatters.get(key)
  if (cached) return cached
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  })
  wallClockFormatters.set(key, formatter)
  return formatter
}

const wallClockAt = (at: number, timeZone: string | undefined): number => {
  const parts = wallClockFormatter(timeZone).formatToParts(at)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value)
  return Date.UTC(
    part("year"),
    part("month") - 1,
    part("day"),
    part("hour"),
    part("minute"),
    part("second"),
  )
}

const warsawOffsetAt = (at: number): number => {
  const wholeSecond = Math.floor(at / 1000) * 1000
  return wallClockAt(wholeSecond, GPS_TIME_ZONE) - wholeSecond
}

const offsetMs = (offset: string): number => {
  if (offset === "Z") return 0
  const sign = offset.startsWith("-") ? -1 : 1
  const digits = offset.slice(1).replace(":", "")
  return (
    sign *
    (Number(digits.slice(0, 2)) * HOUR_MS + Number(digits.slice(2)) * MINUTE_MS)
  )
}

const warsawWallClockToInstant = (wallClock: number): number => {
  const before = warsawOffsetAt(wallClock - 3 * HOUR_MS)
  const after = warsawOffsetAt(wallClock + 3 * HOUR_MS)
  const matching = [before, after]
    .map((offset) => wallClock - offset)
    .filter((instant) => wallClock - warsawOffsetAt(instant) === instant)
  return matching.length > 0 ? Math.min(...matching) : wallClock - before
}

export const parseWarsawTime = (
  value: string | null | undefined,
): number | null => {
  const match = value ? ISO_TIME.exec(value.trim()) : null
  if (!match) return null
  const [, year, month, day, hour, minute, second, fraction, offset] = match
  const wallClock = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second ?? 0),
    Number((fraction ?? "0").padEnd(3, "0").slice(0, 3)),
  )
  const roundTrip = new Date(wallClock)
  if (
    roundTrip.getUTCMonth() !== Number(month) - 1 ||
    roundTrip.getUTCDate() !== Number(day) ||
    roundTrip.getUTCHours() !== Number(hour)
  ) {
    return null
  }
  return offset
    ? wallClock - offsetMs(offset)
    : warsawWallClockToInstant(wallClock)
}

const calendarDay = (at: number, timeZone: string | undefined): number =>
  Math.floor(wallClockAt(at, timeZone) / DAY_MS)

export const isSameWarsawDay = (a: number, b: number): boolean =>
  calendarDay(a, GPS_TIME_ZONE) === calendarDay(b, GPS_TIME_ZONE)

const twoDigits = (value: number) => String(value).padStart(2, "0")

export const formatClockTime = (
  at: number,
  now: number,
  timeZone?: string,
): string => {
  const wallClock = new Date(wallClockAt(at, timeZone))
  const time = `${twoDigits(wallClock.getUTCHours())}:${twoDigits(wallClock.getUTCMinutes())}`
  if (calendarDay(at, timeZone) === calendarDay(now, timeZone)) return time
  const date = `${twoDigits(wallClock.getUTCDate())}.${twoDigits(wallClock.getUTCMonth() + 1)}.${wallClock.getUTCFullYear()}`
  return `${date}, ${time}`
}

export const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(Math.max(0, seconds) / 60)
  return `${Math.floor(minutes / 60)}h ${twoDigits(minutes % 60)}m`
}
